/**
 * Represents a table component that displays pending users. Renders its own
 * `tailwindClassnames.tableCard` surface under the section heading —
 * consumers (org/project users tabs) must not wrap it in another card
 * container.
 */
import { OrganizationUserDto } from "./ObjectTypes";
import { useTranslation } from "react-i18next";
import { Delete, SendIcon } from "./ProjectIcons";
import tailwindClassnames from "./TailwindClassnames";

type PendingUsersTableProps = {
  pendingUsers: OrganizationUserDto[];
  onResendInvitation: Function;
  onRemoveInvitation: Function;
  getRemoveOrResendInvitePermissionFromUserRole: Function;
};

// Quiet icon-only row action (resend/remove invitation) — a ghost-style
// square hit target sized for a single icon.
const rowActionButton =
  "inline-flex items-center justify-center rounded-lg p-2 text-primary-off-black transition duration-150 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue dark:text-primary-off-white dark:hover:bg-white/10";

const PendingUsersTable: React.FC<PendingUsersTableProps> = ({
  pendingUsers,
  onResendInvitation,
  onRemoveInvitation,
  getRemoveOrResendInvitePermissionFromUserRole: getPermissionFromUserRole,
}) => {
  const { t } = useTranslation();

  return (
    <div className="">
      <h2 className={tailwindClassnames.basicSubHeading}>Pending Users</h2>
      <div className={tailwindClassnames.tableCard}>
        <table className="w-full">
          <thead>
            <tr>
              <th className={tailwindClassnames.tableCardHeadCell}>
                {t("Email")}
              </th>
              <th className={tailwindClassnames.tableCardHeadCell}>
                {t("Role")}
              </th>
              <th
                className={`${tailwindClassnames.tableCardHeadCell} text-center`}
              >
                {t("Resend Invitation")}
              </th>
              <th
                className={`${tailwindClassnames.tableCardHeadCell} text-center`}
              >
                {t("Remove User")}
              </th>
            </tr>
          </thead>
          <tbody>
            {pendingUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-4 text-center text-off-black-light dark:text-off-white-dark transition duration-300"
                >
                  {t("No Data Found")}
                </td>
              </tr>
            ) : (
              pendingUsers.map((user, index) => {
                return (
                  <tr
                    key={index}
                    className={tailwindClassnames.tableCardRowStatic}
                  >
                    <td
                      className={tailwindClassnames.tableCardCellPrimary}
                      title={user.email ?? undefined}
                    >
                      {user.email}
                    </td>
                    <td
                      className={tailwindClassnames.tableCardCell}
                      title={t(user.role_name) || undefined}
                    >
                      {t(user.role_name)}
                    </td>
                    {/* Conditional rendering, NOT the hidden attribute — the
                        rowActionButton class sets a display utility, which
                        outranks Tailwind preflight's [hidden] rule and would
                        leave these permission-gated buttons visible. */}
                    <td className="px-4 py-2 text-center">
                      {getPermissionFromUserRole(user.role_id) && (
                        <button
                          type="button"
                          className={rowActionButton}
                          onClick={() => onResendInvitation(user.user_id)}
                        >
                          <SendIcon />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {getPermissionFromUserRole(user.role_id) && (
                        <button
                          type="button"
                          className={rowActionButton}
                          onClick={() => onRemoveInvitation(user.user_id)}
                        >
                          <Delete />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PendingUsersTable;
